// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Konvo following a Teams meeting in the desktop app's Teams window (Teams' own web app).
//
// The Teams window reads the live captions Teams shows (when the person turns captions on) and
// sends each line here. A line is finished when it has not changed for a few seconds, or when the
// same speaker starts a new one. While the meeting runs, Konvo's assistant looks at the latest
// finished lines about every 45 seconds (POST /konvo/teams/meetings/assist) and shows a card only
// when something said connects to a task, a date or the meeting brief. When the window closes or
// opens the next meeting, the lines go to POST /konvo/teams/meetings/recap: the recap card (and
// the transcript, unless the person said not to keep one) lands in their Konvo chat. All of it
// runs on the person's own assistant plan; nothing is billed to Nuvio.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import KonvoCalendar from '@app/features/konvo_calendar/state/KonvoCalendar';
import KonvoUpNext from '@app/features/konvo_calendar/state/KonvoUpNext';
import {
	type CaptionEvent,
	type FollowLine,
	foldCaption,
	settleLines,
} from '@app/features/konvo_teams_meetings/utils/TeamsFollowLines';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const logger = new Logger('TeamsFollow');
const PREFS_KEY = 'konvo.teamsFollow.prefs';
const LOOK_EVERY_MS = 45_000;
const LOOK_WINDOW_MS = 3 * 60_000;
const LOOK_MAX_LINES = 40;
/** The captions tip shows when a meeting has run this long without captions. */
const TIP_AFTER_MS = 90_000;
export interface FollowCard {
	text: string;
	task: {id: string; name: string} | null;
}

export interface FollowMeeting {
	url: string;
	title: string | null;
	startedAt: number;
	eventId: string | null;
	eventStart: string | null;
}

function readFollowPref(): boolean {
	try {
		const raw = getProtectedLocalStorage()?.getItem(PREFS_KEY);
		return raw ? (JSON.parse(raw) as {follow?: boolean}).follow !== false : true;
	} catch {
		return true;
	}
}

class TeamsFollowStore {
	meeting: FollowMeeting | null = null;
	lines: Array<FollowLine> = [];
	/** Konvo follows Teams meetings (remembered; on by default). */
	following = true;
	/** This meeting's recap comes with the transcript (asked again for every meeting). */
	keepTranscript = true;
	/** Konvo stopped following this meeting only. */
	pausedThisMeeting = false;
	card: FollowCard | null = null;
	tipDismissed = false;
	noticeDismissed = false;
	now = Date.now();

	private started = false;
	private lookTimer: ReturnType<typeof setInterval> | null = null;
	private tickTimer: ReturnType<typeof setInterval> | null = null;
	private sentId = 0;
	private shown: Array<string> = [];
	private looking = false;

	constructor() {
		makeAutoObservable<TeamsFollowStore, 'started' | 'lookTimer' | 'tickTimer' | 'sentId' | 'shown' | 'looking'>(
			this,
			{
				started: false,
				lookTimer: false,
				tickTimer: false,
				sentId: false,
				shown: false,
				looking: false,
				lines: observable.ref,
				card: observable.ref,
			},
			{autoBind: true},
		);
		this.following = readFollowPref();
	}

	get active(): boolean {
		return this.following && !this.pausedThisMeeting && this.meeting !== null;
	}

	get captionsSeen(): boolean {
		return this.lines.length > 0;
	}

	/** The captions tip: a meeting has run a while and no captions came. */
	get showCaptionsTip(): boolean {
		return (
			this.active &&
			!this.captionsSeen &&
			!this.tipDismissed &&
			this.now - (this.meeting?.startedAt ?? 0) > TIP_AFTER_MS
		);
	}

	/** The "Konvo is following" notice once captions arrive, with the meeting's choices. */
	get showNotice(): boolean {
		return this.active && this.captionsSeen && !this.noticeDismissed && this.card === null;
	}

	/** Listens to the desktop app's Teams window (once). */
	start(): void {
		if (this.started) return;
		const desktop = getElectronAPI();
		if (!desktop?.onTeamsMeeting) return;
		this.started = true;
		desktop.onTeamsMeeting((event) => {
			if (event.type === 'start') this.onStart(event.url, event.title);
			else if (event.type === 'caption') this.onCaption(event);
			else this.onEnd();
		});
	}

	private onStart(url: string, title: string | null): void {
		if (this.meeting) this.onEnd();
		const event = [...KonvoUpNext.events, ...KonvoCalendar.events].find((e) => e.teams_join_url === url);
		this.meeting = {
			url,
			title: title ?? event?.subject ?? null,
			startedAt: Date.now(),
			eventId: event?.id ?? null,
			eventStart: event?.start ?? null,
		};
		this.lines = [];
		this.card = null;
		this.keepTranscript = true;
		this.pausedThisMeeting = false;
		this.tipDismissed = false;
		this.noticeDismissed = false;
		this.sentId = 0;
		this.shown = [];
		this.lookTimer = setInterval(() => void this.look(), LOOK_EVERY_MS);
		this.tickTimer = setInterval(() => this.tick(), 2_000);
	}

	private onCaption(caption: CaptionEvent): void {
		if (!this.meeting || !this.following || this.pausedThisMeeting) return;
		this.lines = foldCaption(this.lines, caption, Date.now());
	}

	private tick(): void {
		this.now = Date.now();
		const settled = settleLines(this.lines, this.now);
		if (settled) this.lines = settled;
	}

	private onEnd(): void {
		const meeting = this.meeting;
		if (this.lookTimer) clearInterval(this.lookTimer);
		if (this.tickTimer) clearInterval(this.tickTimer);
		this.lookTimer = null;
		this.tickTimer = null;
		const lines = this.lines.map((line) => ({...line, final: true}));
		const recap = meeting && this.following && !this.pausedThisMeeting && lines.length >= 5;
		const keep = this.keepTranscript;
		this.meeting = null;
		this.lines = [];
		this.card = null;
		if (!recap || !meeting) return;
		void http
			.post(Endpoints.KONVO_TEAMS_MEETINGS_RECAP, {
				body: {
					subject: meeting.title,
					started_at: meeting.startedAt,
					ended_at: Date.now(),
					keep_transcript: keep,
					lines: lines.map((line) => ({speaker: line.speaker, text: line.text, at: line.at})),
				},
			})
			.catch((error) => logger.warn('meeting recap request failed', error));
	}

	/** Sends the latest finished lines when at least two are new since the last look. */
	private async look(): Promise<void> {
		const meeting = this.meeting;
		if (this.looking || !meeting || !this.active) return;
		const finals = this.lines.filter((line) => line.final);
		if (finals.filter((line) => line.id > this.sentId).length < 2) return;
		const since = Date.now() - LOOK_WINDOW_MS;
		const lines = finals.filter((line) => line.at >= since).slice(-LOOK_MAX_LINES);
		this.sentId = Math.max(...finals.map((line) => line.id));
		this.looking = true;
		try {
			const response = await http.post<{card?: FollowCard | null}>(Endpoints.KONVO_TEAMS_MEETINGS_ASSIST, {
				body: {
					subject: meeting.title,
					event_id: meeting.eventId,
					event_start: meeting.eventStart,
					lines: lines.map((line) => ({speaker: line.speaker, text: line.text})),
					shown: this.shown,
				},
			});
			const card = response.body?.card ?? null;
			if (card?.text && this.meeting === meeting && this.active) {
				this.shown = [...this.shown, card.text].slice(-10);
				runInAction(() => (this.card = card));
			}
		} catch (error) {
			logger.info('meeting look failed', error);
		} finally {
			this.looking = false;
		}
	}

	dismissCard(): void {
		this.card = null;
	}

	dismissTip(): void {
		this.tipDismissed = true;
	}

	dismissNotice(): void {
		this.noticeDismissed = true;
	}

	setKeepTranscript(keep: boolean): void {
		this.keepTranscript = keep;
	}

	/** Stops following this meeting: no cards, no recap, and the captions so far are dropped. */
	stopThisMeeting(): void {
		this.pausedThisMeeting = true;
		this.lines = [];
		this.card = null;
	}
}

const store = new TeamsFollowStore();

// Dev builds (the sandbox) can drive the corner cards from a script.
if (import.meta.env.DEV && typeof window !== 'undefined') {
	(window as unknown as {__konvoTeamsFollow?: TeamsFollowStore}).__konvoTeamsFollow = store;
}

export default store;
